import { Controller } from "@hotwired/stimulus"

export default class extends Controller {
  static values = { 
    group: String,
    animation: { type: Number, default: 150 },
    savePositions: { type: Boolean, default: false },
    updateUrl: String
  }

  connect() {
    this.initializeSortable()
  }

  disconnect() {
    if (this.sortable) {
      this.sortable.destroy()
    }
  }

  async initializeSortable() {
    try {
      // Load saved positions first (only if savePositions is enabled)
      if (this.savePositionsValue) {
        this.loadSavedPositions()
      }

      // Import SortableJS dynamically
      const { default: Sortable } = await import('sortablejs')
      
      this.sortable = Sortable.create(this.element, {
        group: this.groupValue || 'shared-sortable',
        animation: this.animationValue,
        ghostClass: 'sortable-ghost',
        chosenClass: 'sortable-chosen', 
        dragClass: 'sortable-drag',
        onEnd: (evt) => this.handleSortEnd(evt)
      })

      // SortableJS initialized successfully
    } catch (error) {
      console.error('Failed to load SortableJS:', error)
    }
  }

  handleSortEnd(evt) {
    if (this.savePositionsValue) {
      // Save positions to localStorage (admin pages pattern)
      this.saveAllPositions()
    } else if (this.updateUrlValue) {
      // Send AJAX update (galleries/task lists pattern)
      this.sendUpdateRequest()
    }
  }

  // Admin pages pattern - save to localStorage
  saveAllPositions() {
    const allContainers = document.querySelectorAll(`[data-sortable-group-value="${this.groupValue}"]`)
    
    allContainers.forEach(container => {
      Array.from(container.children).forEach((child) => {
        if (child.id) {
          this.savePosition(child.id)
        }
      })
    })
  }

  savePosition(itemId) {
    const element = document.getElementById(itemId)
    if (!element) return

    const container = element.parentElement
    const index = Array.from(container.children).indexOf(element)
    
    const position = {
      container: container.id,
      index: index
    }
    
    localStorage.setItem(itemId, JSON.stringify(position))
  }

  loadSavedPositions() {
    Array.from(this.element.children).forEach((child) => {
      if (child.id) {
        this.loadPosition(child.id)
      }
    })
  }

  loadPosition(itemId) {
    const position = JSON.parse(localStorage.getItem(itemId) || 'null')
    if (!position) return

    const element = document.getElementById(itemId)
    const targetContainer = document.getElementById(position.container)
    
    if (!element || !targetContainer) return

    const targetIndex = position.index
    const targetChildren = Array.from(targetContainer.children)

    if (targetIndex === 0) {
      targetContainer.prepend(element)
    } else if (targetIndex >= targetChildren.length) {
      targetContainer.append(element)
    } else {
      targetChildren[targetIndex - 1].after(element)
    }
  }

  // Galleries/Task lists pattern - send AJAX update
  sendUpdateRequest() {
    if (!this.updateUrlValue) return

    const order = Array.from(this.element.children).map(child => {
      return child.getAttribute('order') || child.dataset.order || child.id
    }).filter(Boolean)

    // Get parent context for the update
    const parentType = this.element.dataset.parentType
    const parentId = this.element.dataset.parentId
    const methodChain = this.element.dataset.methodChain?.split(',')

    // Use form data format like the original jQuery AJAX
    const formData = new FormData()
    formData.append('authenticity_token', document.querySelector('[name="csrf-token"]').content)
    
    order.forEach((id) => {
      formData.append('order[]', id)
    })
    
    if (parentType) formData.append('parent_type', parentType)
    if (parentId) formData.append('parent_id', parentId)
    if (methodChain) {
      // Send as array, not hash
      methodChain.forEach((item) => {
        formData.append('items_method_chain[]', item)
      })
    }

    fetch(this.updateUrlValue, {
      method: 'POST',
      body: formData
    }).then(response => {
      console.log('Update response:', response)
      if (response.ok) {
        console.log('Sort order updated successfully')
      } else {
        console.error('Server returned error:', response.status, response.statusText)
      }
    }).catch(error => {
      console.error('Failed to update sort order:', error)
    })
  }
}